/**
 * Keywords AI Auth Utilities
 * 
 * Shared authentication utilities for Keywords AI integration.
 * 
 * Architecture: This module handles two execution contexts:
 * 1. **Taskpane context**: Uses GlobalStateManager → authManager → getTempKeywordsKey()
 * 2. **Dialog context** (e.g., admin panel): GlobalStateManager not initialized,
 *    so we fall back to authService (shared package) which has JWT from localStorage
 * 
 * The dialog context requires a direct API call to the Carousel auth endpoint
 * since it doesn't have the full authManager infrastructure.
 */

import { authService } from '@carousel-office/shared';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const CAROUSEL_AUTH_BASE = 'https://app.carouseltech.io';
const KEYWORDS_KEY_CACHE_BUFFER_MS = 3 * 60 * 1000; // 3 minutes before expiry

// Simple in-memory cache for dialog context (avoids repeated API calls)
let keywordsKeyCache: { key: string; expiresAt: number } | null = null;

// ═══════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get a temporary Keywords AI API key using the existing auth system.
 * 
 * This function tries two approaches in order:
 * 1. GlobalStateManager (taskpane context) - uses authManager.getTempKeywordsKey()
 * 2. authService (dialog context) - makes direct API call with JWT token
 * 
 * @returns The API key string, or null if auth is not ready or fails
 * 
 * @example
 * const apiKey = await getKeywordsApiKey();
 * if (!apiKey) {
 *     console.warn('Auth not ready, will retry later');
 *     return;
 * }
 * // Use apiKey for Keywords AI requests
 */
export async function getKeywordsApiKey(): Promise<string | null> {
    // ═══════════════════════════════════════════════════════════════════════
    // APPROACH 1: Try GlobalStateManager (taskpane context)
    // ═══════════════════════════════════════════════════════════════════════
    try {
        const { default: GlobalStateManager } = await import('../../main/globals/global-state/globalStateManager');
        const gsm = GlobalStateManager.getInstance();
        
        // Check if auth is ready (GSM initialized with JWT + authManager)
        if (gsm.isAuthReady()) {
            const authManager = gsm.getAuthManager();
            const jwtToken = gsm.getJwtToken();
            
            if (authManager && jwtToken) {
                console.log('[KeywordsAuth] Using GlobalStateManager auth');
                return await authManager.getTempKeywordsKey(jwtToken);
            }
        }
    } catch (error) {
        // GSM might not be available or ready - this is expected in dialog context
        console.log('[KeywordsAuth] GlobalStateManager not available, trying authService fallback');
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // APPROACH 2: Fall back to authService (dialog context)
    // ═══════════════════════════════════════════════════════════════════════
    try {
        // Ensure authService is initialized (restores tokens from localStorage)
        // This is needed because in dialog context, authService is a fresh singleton
        await authService.initialize();
        
        const jwtToken = authService.getAccessToken();
        
        if (!jwtToken) {
            console.log('[KeywordsAuth] No JWT token available from authService');
            return null;
        }
        
        // Check cache first
        const now = Date.now();
        if (keywordsKeyCache && (keywordsKeyCache.expiresAt - KEYWORDS_KEY_CACHE_BUFFER_MS) > now) {
            console.log('[KeywordsAuth] Using cached Keywords API key (dialog context)');
            return keywordsKeyCache.key;
        }
        
        // Make direct API call to get temporary Keywords key
        console.log('[KeywordsAuth] Fetching Keywords API key via authService JWT');
        const response = await fetch(`${CAROUSEL_AUTH_BASE}/keywords/get-temporary-key`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json',
            },
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error(`[KeywordsAuth] Failed to get Keywords API key: ${response.status} ${errorText}`);
            return null;
        }
        
        const data = await response.json();
        const tempKey = data.api_key || data.key || data.temp_key;
        
        if (!tempKey) {
            console.error('[KeywordsAuth] No API key in response');
            return null;
        }
        
        // Cache the key
        let expiresAt: number;
        if (data.expiry_date) {
            expiresAt = new Date(data.expiry_date).getTime();
        } else if (data.expires_in) {
            expiresAt = Date.now() + (data.expires_in * 1000);
        } else {
            expiresAt = Date.now() + 3600000; // Fallback to 1 hour
        }
        
        keywordsKeyCache = { key: tempKey, expiresAt };
        console.log(`[KeywordsAuth] ✅ Retrieved Keywords API key via authService (expires: ${new Date(expiresAt).toISOString()})`);
        
        return tempKey;
        
    } catch (error) {
        console.error('[KeywordsAuth] Error getting API key via authService:', 
            error instanceof Error ? error.message : error);
        return null;
    }
}

// REMOVED: clearKeywordsKeyCache - no callers
